import { extractJsonBlock, repairInnerQuotes } from "@shared/stories-json";

export type StoriesKeywordStyle =
  | "red-text"
  | "underline"
  | "highlight-box"
  | "circle"
  | "arrow-underline";

export interface StoriesKeywordHighlight {
  keyword: string;
  style: StoriesKeywordStyle;
}

export interface ParsedStoryFrame {
  index: number;
  role: string;
  headline: string;
  body?: string;
  cta?: { text?: string; replyKeyword?: string };
  visualPrompt: string;
  /** Combined view used by the preview's old highlightText helper. */
  keywordHighlights?: StoriesKeywordHighlight[];
  /** Original split lists from the server schema — used by the edit modal. */
  headlineKeywords?: StoriesKeywordHighlight[];
  bodyKeywords?: StoriesKeywordHighlight[];
  overlayLayout?: "top" | "middle" | "bottom" | "split";
  textColor?: "white" | "black";
}

export interface ParsedStoriesScript {
  language?: string;
  flow?: string;
  visualStyleNote?: string;
  sharedBackgroundPrompt?: string;
  frames: ParsedStoryFrame[];
}

interface RawKeyword {
  text?: string;
  keyword?: string;
  style?: StoriesKeywordStyle;
}

function normalizeKeywords(arr: unknown): StoriesKeywordHighlight[] {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((k) => {
      const r = k as RawKeyword;
      const keyword = r.text ?? r.keyword ?? "";
      const style = (r.style ?? "red-text") as StoriesKeywordStyle;
      return keyword ? { keyword, style } : null;
    })
    .filter((k): k is StoriesKeywordHighlight => k !== null);
}

/**
 * VP-230 4a: heurystyka „model próbował zwrócić skrypt stories, ale JSON jest
 * nie do naprawienia". Pozwala pokazać czytelny błąd + retry zamiast zrzucać
 * użytkownikowi surowy JSON jako zwykły tekst wiadomości.
 */
export function looksLikeStoriesScriptAttempt(content: string): boolean {
  if (!content) return false;
  return (
    /"frames"\s*:/.test(content) && /"(visualPrompt|sharedBackgroundPrompt|headline)"/.test(content)
  );
}

export function parseStoriesScript(content: string): ParsedStoriesScript | null {
  if (!content) return null;
  const extracted = extractJsonBlock(content);
  if (!extracted) return null;
  let jsonText = extracted;
  let parsed: { frames?: unknown[] } & Record<string, unknown>;
  try {
    parsed = JSON.parse(jsonText) as { frames?: unknown[] } & Record<string, unknown>;
  } catch {
    // Fallback: repair unescaped inner quotes (typical LLM output mistake)
    try {
      jsonText = repairInnerQuotes(jsonText);
      parsed = JSON.parse(jsonText) as { frames?: unknown[] } & Record<string, unknown>;
    } catch {
      return null;
    }
  }
  try {
    if (!Array.isArray(parsed.frames) || parsed.frames.length === 0) return null;
    const frames: ParsedStoryFrame[] = parsed.frames.map((f) => {
      const frame = f as Record<string, unknown>;
      const headlineKeywords = normalizeKeywords(frame.headlineKeywords);
      const bodyKeywords = normalizeKeywords(frame.bodyKeywords);
      const flatLegacy = normalizeKeywords(frame.keywordHighlights);
      const combined = [...flatLegacy, ...headlineKeywords, ...bodyKeywords];
      return {
        index: Number(frame.index ?? 1),
        role: String(frame.role ?? "custom"),
        headline: String(frame.headline ?? ""),
        body: typeof frame.body === "string" ? frame.body : undefined,
        cta: (frame.cta as ParsedStoryFrame["cta"]) ?? undefined,
        visualPrompt: String(frame.visualPrompt ?? ""),
        headlineKeywords,
        bodyKeywords,
        keywordHighlights: combined,
        overlayLayout:
          (frame.overlayLayout as ParsedStoryFrame["overlayLayout"]) ?? ("center" as never),
        textColor: (frame.textColor as ParsedStoryFrame["textColor"]) ?? "white",
      };
    });
    return {
      language: parsed.language as string | undefined,
      flow: parsed.flow as string | undefined,
      visualStyleNote: parsed.visualStyleNote as string | undefined,
      sharedBackgroundPrompt: parsed.sharedBackgroundPrompt as string | undefined,
      frames,
    };
  } catch {
    return null;
  }
}
